/**
 * 编组元素组件
 *
 * 两种模式：
 *  普通模式 — 整体命中，click/mousedown 代理到组本身
 *  组内编辑模式（isEditingGroup=true）— 子元素各自独立交互，
 *    组背景设为 pointer-events-none（点击透穿到画布），子元素保持 pointer-events-auto
 *
 * groupEditContext 从 Workspace 逐层透传，使任意深度的嵌套组都能感知
 * 当前正在编辑的组，并正确渲染其子元素为可交互模式。
 */
import type {
  Element,
  GroupElement as GroupElementType,
  TextElement as TextElementType,
  ImageElement as ImageElementType,
  TableElement as TableElementType,
  LineElement as LineElementType,
  RectangleElement as RectangleElementType,
  CircleElement as CircleElementType,
  TriangleElement as TriangleElementType,
  VectorElement as VectorElementType,
  FreehandElement as FreehandElementType,
} from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps, TextEditOptions } from '@/components/canvas/elements/types';
import { TextElement } from '../text/TextElement';
import { ImageElement } from '../image/ImageElement';
import { TableElement } from '../table/TableElement';
import {
  LineElement,
  RectangleElement,
  CircleElement,
  TriangleElement,
  VectorElement,
  FreehandElement,
} from '../shape';
import { appendBlurFilter, composeContainerTransform } from './containerTransform';
import { blurRadius } from '@/components/canvas/elements/model';

/**
 * 深层组编辑上下文：从 Workspace 向下透传，供任意嵌套深度的 GroupElement 判断
 * 哪个子组正处于编辑模式，并传递对应的交互 props。
 */
export interface GroupEditContext {
  /** 当前处于编辑模式的组 ID（可能是任意深度的嵌套组） */
  editingGroupId: string | null;
  /** 编辑模式下选中的子元素 ID 列表 */
  selectedIds: string[];
  /** 编辑模式下的拖拽偏移（画布单位） */
  dragOffset: { dx: number; dy: number };
  /** 编辑模式下正在文本编辑的子元素 ID */
  editingTextId: string | null;
  /** 子元素 mousedown 回调（用于拖拽） */
  onChildMouseDown: (e: React.MouseEvent, child: Element) => void;
  /** 文本子元素开始编辑回调 */
  onChildStartEdit: (id: string, opts?: TextEditOptions) => void;
  /** 图片子元素双击进入「移动图片在容器内位置」模式 */
  onStartImagePositionDrag?: (element: ImageElementType, e: React.MouseEvent) => void;
}

export interface GroupElementProps extends ElementComponentProps<GroupElementType> {
  allElements: Element[];
  selectElement: (id: string, addToSelection: boolean) => void;
  /** 组内编辑模式 */
  isEditingGroup?: boolean;
  /** 编辑模式下，当前选中的子元素 ID 列表 */
  selectedChildIds?: string[];
  /** 编辑模式下，选中子元素的拖拽偏移 */
  childDragOffset?: { dx: number; dy: number };
  /** 编辑模式下，正在文本编辑的子元素 ID */
  editingTextId?: string | null;
  /** 编辑模式下，子元素 mousedown → 拖拽 */
  onChildMouseDown?: (e: React.MouseEvent, child: Element) => void;
  /** 编辑模式下，文本子元素进入编辑 */
  onChildStartEdit?: (id: string, opts?: TextEditOptions) => void;
  /** 深层组编辑上下文，从 Workspace 透传 */
  groupEditContext?: GroupEditContext;
}

export function GroupElement({
  element: group,
  selected,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
  allElements,
  selectElement,
  isEditingGroup = false,
  selectedChildIds = [],
  childDragOffset = { dx: 0, dy: 0 },
  editingTextId,
  onChildMouseDown,
  onChildStartEdit,
  groupEditContext,
}: GroupElementProps) {
  const { dx, dy } = dragOffset;
  const children = group.data.children
    .map((id) => allElements.find((el) => el.id === id))
    .filter(Boolean) as Element[];
  const sorted = [...children].sort((a, b) => a.layout.zIndex - b.layout.zIndex);

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    left: group.layout.x + dx,
    top: group.layout.y + dy,
    width: group.size.width,
    height: group.size.height,
    // 组容器变换：旋转/翻转/3D
    transform: composeContainerTransform(group),
    filter: appendBlurFilter(undefined, blurRadius(group)),
    // 编辑模式：背景透穿（子元素保持 pointer-events-auto）
    pointerEvents: isEditingGroup ? 'none' : 'auto',
  };

  if (!isEditingGroup) {
    // ── 普通模式 ──────────────────────────────────────────────
    const commonVisual = {
      selected: false as const,
      dragOffset: { dx: 0, dy: 0 } as const,
      onSelect: (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect(e);
      },
    };

    // 若有直接子组正处于编辑状态，本组进入"透传模式"：
    // 渲染该子组为编辑状态，去掉透明遮罩，让鼠标事件穿透到子组的子元素。
    const editingDirectChildId =
      groupEditContext?.editingGroupId &&
      group.data.children.includes(groupEditContext.editingGroupId)
        ? groupEditContext.editingGroupId
        : null;

    if (editingDirectChildId) {
      return (
        <div style={wrapperStyle} className="cursor-inherit">
          {sorted.map((el) => {
            if (el.id === editingDirectChildId && el.type === 'group') {
              // 以编辑模式渲染该子组，继承上下文中的交互 props
              return (
                <GroupElement
                  key={el.id}
                  element={el as GroupElementType}
                  selected={false}
                  dragOffset={{ dx: 0, dy: 0 }}
                  onSelect={(e) => e.stopPropagation()}
                  onMouseDown={(e, child) => groupEditContext!.onChildMouseDown(e, child)}
                  allElements={allElements}
                  selectElement={selectElement}
                  isEditingGroup={true}
                  selectedChildIds={groupEditContext!.selectedIds}
                  childDragOffset={groupEditContext!.dragOffset}
                  editingTextId={groupEditContext!.editingTextId}
                  onChildMouseDown={groupEditContext!.onChildMouseDown}
                  onChildStartEdit={groupEditContext!.onChildStartEdit}
                  groupEditContext={groupEditContext}
                />
              );
            }
            return renderChild(el, commonVisual, allElements, selectElement, groupEditContext);
          })}
          {/* 透传模式：去掉遮罩，让鼠标事件穿透到正在编辑的子组 */}
        </div>
      );
    }

    return (
      <div style={wrapperStyle} className="cursor-inherit">
        {sorted.map((el) => renderChild(el, commonVisual, allElements, selectElement, groupEditContext))}
        {/* 透明交互遮罩：覆盖所有子元素，捕获 hover / drag / dblclick */}
        <div
          className="absolute inset-0"
          style={{ zIndex: 9999 }}
          data-element-hit
          data-element-id={group.id}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(e);
          }}
          onMouseDown={(e) => onMouseDown?.(e, group)}
          // 不设 onDoubleClick，让 dblclick 自然冒泡到画布层
        />
      </div>
    );
  }

  // ── 组内编辑模式 ──────────────────────────────────────────
  return (
    <div
      style={wrapperStyle}
      // data-group-editing-id 用于 Workspace 识别组边界（click-outside 检测）
      data-group-editing-id={group.id}
    >
      {/* 虚线组边界由 Workspace 在 canvas 坐标层动态渲染，此处不再内嵌 */}

      {sorted.map((el) => {
        if (el.id === editingTextId) return null; // 文本编辑时由 TextEditOverlay 渲染
        const isChildSelected = selectedChildIds.includes(el.id);
        const offset = isChildSelected ? childDragOffset : { dx: 0, dy: 0 };

        const childProps: ChildProps = {
          selected: isChildSelected,
          dragOffset: offset,
          onSelect: (e: React.MouseEvent) => {
            e.stopPropagation();
            if (el.type === 'text') selectElement(el.id, false); // 文本：第一次点击由 onClick 触发选中
          },
          onMouseDown: (e: React.MouseEvent, child: Element) => {
            onChildMouseDown?.(e, child);
          },
          onStartImagePositionDrag: groupEditContext?.onStartImagePositionDrag,
        };

        if (el.type === 'text') {
          return (
            <TextElement
              key={el.id}
              element={el as TextElementType}
              {...childProps}
              onStartEdit={onChildStartEdit}
            />
          );
        }
        return renderChild(el, childProps, allElements, selectElement, groupEditContext);
      })}
    </div>
  );
}

// ── 子元素渲染工具 ─────────────────────────────────────────────────────────────

type ChildProps = {
  selected: boolean;
  dragOffset: { dx: number; dy: number };
  onSelect: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent, el: Element) => void;
  onStartImagePositionDrag?: (element: ImageElementType, e: React.MouseEvent) => void;
};

/**
 * 渲染单个子元素，支持任意类型（含嵌套组）。
 * allElements / selectElement 用于嵌套组的递归渲染。
 * groupEditContext 向下透传，供深层嵌套组感知编辑状态。
 */
function renderChild(
  el: Element,
  props: ChildProps,
  allElements: Element[] = [],
  selectElement: (id: string, multi: boolean) => void = () => {},
  groupEditContext?: GroupEditContext,
): React.ReactNode {
  switch (el.type) {
    case 'text':
      return <TextElement key={el.id} element={el as TextElementType} {...props} />;
    case 'image':
      return (
        <ImageElement
          key={el.id}
          element={el as ImageElementType}
          {...props}
          onStartImagePositionDrag={props.onStartImagePositionDrag}
        />
      );
    case 'table':
      return <TableElement key={el.id} element={el as TableElementType} {...props} />;
    case 'line':
      return <LineElement key={el.id} element={el as LineElementType} {...props} />;
    case 'rect':
      return <RectangleElement key={el.id} element={el as RectangleElementType} {...props} />;
    case 'circle':
      return <CircleElement key={el.id} element={el as CircleElementType} {...props} />;
    case 'triangle':
      return <TriangleElement key={el.id} element={el as TriangleElementType} {...props} />;
    case 'vector':
      return <VectorElement key={el.id} element={el as VectorElementType} {...props} />;
    case 'freehand':
      return <FreehandElement key={el.id} element={el as FreehandElementType} {...props} />;
    case 'group':
      // 嵌套组：递归渲染，GroupElement 会根据 groupEditContext 自动决定是否进入编辑模式
      return (
        <GroupElement
          key={el.id}
          element={el as GroupElementType}
          selected={props.selected}
          dragOffset={props.dragOffset}
          onSelect={props.onSelect}
          onMouseDown={props.onMouseDown}
          allElements={allElements}
          selectElement={selectElement}
          groupEditContext={groupEditContext}
        />
      );
    default:
      return null;
  }
}
